import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { Processor } from '../../models';
import { AuthenticationService, ServicesService, ProcessorService } from '../../services';

const JOBS_FILTER_STORAGE_KEY = 'prism-jobs-filter';

@Component({
  selector: 'prism-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.scss']
})
export class JobsComponent implements OnInit, OnDestroy {
  services$: Observable<any[]> = this.servicesService.services$;
  servicesLoading$: Observable<boolean> = this.servicesService.areServicesLoading$;
  fetchServicesSub: Subscription;
  invoicesQueueCountSub: Subscription;

  filterSelection: string = null;
  pageIndex = 0;
  private pageSize = 10;

  get includeLuxEngine() {
    return this.filterSelection === 'Include_LuxEngine';
  }

  get hasInvoicingPermissions() {
    return this.authService.hasInvoicingPermissions;
  }

  get notInvoicedCounterValue() {
    return this.servicesService.notInvoicedCounter;
  }

  get includeLuxEngineCounterValue() {
    return this.servicesService.includeLuxEngineCounter;
  }

  get tableServicesCount() {
    switch (this.filterSelection) {
      case 'Not_Invoiced':
        return this.notInvoicedCounterValue || 0;
      case 'Include_LuxEngine':
        return this.servicesService.serviceCount || 0;
      default:
        return this.servicesService.serviceCount - this.includeLuxEngineCounterValue || 0;
    }
  }

  constructor(
    private processorService: ProcessorService,
    private servicesService: ServicesService,
    private authService: AuthenticationService
  ) {
  }

  ngOnInit() {
    const killProcessorCheckSub$ = new Subject();

    this.processorService.processor$
      .pipe(
        takeUntil(killProcessorCheckSub$)
      )
      .subscribe(
        (processor: Processor) => {

          if (!processor) {
            this.processorService.getProcessor()
              .toPromise()
              .then()
              .catch(e => {
                console.error('Could not fetch Processor:', e);
              });
          }
          killProcessorCheckSub$.next(null);
        },
        error => {
          console.error('Could check for Processor:', error);
        },
        () => {
        }
      );

    this.filterSelection = this.readStoredFilter();
    this.servicesService.setSelectedService(null);
    this.fetchServices({pageIndex: 0, pageSize: 10});
    this.getServicesCount();
  }

  fetchServices({pageIndex, pageSize}) {
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.fetchServicesSub = this.servicesService.getServices({
      pageIndex,
      pageSize,
      notInvoiced: this.filterSelection === 'Not_Invoiced',
      includeLuxEngine: this.includeLuxEngine
    })
      .pipe(
        first(d => !!d)
      )
      .subscribe();
  }

  getServicesCount() {
    this.servicesService.getServicesCount(this.filterSelection === 'Not_Invoiced', this.includeLuxEngine);
  }

  filterJobs(filter: string) {
    this.filterSelection = this.filterSelection === filter ? null : filter;
    this.storeFilter(this.filterSelection);
    this.fetchServices({pageIndex: 0, pageSize: this.pageSize});
    this.getServicesCount();
  }

  private readStoredFilter(): string {
    try {
      const stored = sessionStorage.getItem(JOBS_FILTER_STORAGE_KEY);
      return ['Not_Invoiced', 'Include_LuxEngine'].includes(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private storeFilter(filter: string) {
    try {
      if (filter) {
        sessionStorage.setItem(JOBS_FILTER_STORAGE_KEY, filter);
      } else {
        sessionStorage.removeItem(JOBS_FILTER_STORAGE_KEY);
      }
    } catch {
    }
  }

  onConfirmationSuccess() {
    this.fetchServices({pageIndex: this.pageIndex, pageSize: this.pageSize});
    this.getServicesCount();
  }

  ngOnDestroy() {
    if (!!this.invoicesQueueCountSub) {
      this.invoicesQueueCountSub.unsubscribe();
    }
    if (!!this.fetchServicesSub) {
      this.fetchServicesSub.unsubscribe();
    }
  }
}
